import { useState } from 'react';
import { Crown, User, Mail, Building2, MapPin, FileText, Link as LinkIcon, Loader2, Upload, Image as ImageIcon, Briefcase, BadgePoundSterling } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { submitFeaturedJob, createFeaturedJobCheckout, uploadLogo } from '@/lib/api';

const FeaturedJobPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    employer: '',
    location: '',
    salary: '',
    link: '',
    description: '',
    contact_name: '',
    contact_email: '',
    business_name: '',
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.employer || !formData.location || !formData.link || !formData.contact_name || !formData.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      let imageFileId = null;
      if (imageFile) {
        try {
          const uploadRes = await uploadLogo(imageFile);
          imageFileId = uploadRes.data.file_id;
        } catch { toast.error('Image upload failed, continuing without image'); }
      }

      const res = await submitFeaturedJob({ ...formData, image_file_id: imageFileId });
      const featuredId = res.data.id;
      const checkoutRes = await createFeaturedJobCheckout(featuredId, window.location.origin);
      window.location.href = checkoutRes.data.url;
    } catch (error) {
      console.error('Featured job submission error:', error);
      toast.error(error.response?.data?.detail || 'Submission failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="featured-job-page">
      <SEOPage.Register />
      <Navbar />

      <section className="pt-32 pb-16 bg-gradient-to-b from-amber-500 via-amber-600 to-yellow-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 0%, transparent 40%), radial-gradient(circle at 80% 70%, white 0%, transparent 40%)' }} />
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <Badge className="bg-white/20 text-white border-0 mb-4" data-testid="featured-job-badge">
            <Crown className="h-3 w-3 mr-1" /> Featured Job
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Pin Your Job to Slot #1
          </h1>
          <p className="text-amber-50 text-lg max-w-2xl mx-auto">
            Maximum visibility — your role at the very top of the Jobs Board with a gold border, for 30 days.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white/10 rounded-full px-6 py-3">
            <BadgePoundSterling className="h-6 w-6" />
            <span className="text-3xl font-bold">£30</span>
            <span className="text-amber-100 text-sm">one-off &middot; 30 days featured</span>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { num: '1', title: 'Top of the Board', desc: 'Pinned at slot #1 on the public Jobs Board with a gold border and Featured badge' },
              { num: '2', title: 'More Applicants', desc: 'Featured listings get ~3x more clicks than standard listings' },
              { num: '3', title: '30 Days Active', desc: 'Stays featured for a full 30 days — no auto-expiry after 10 days like regular posts' },
            ].map(item => (
              <Card key={item.num} className="text-center border-amber-100" data-testid={`benefit-${item.num}`}>
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-amber-600">{item.num}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-500 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="shadow-lg" data-testid="featured-job-form-card">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-amber-500" /> Job Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><FileText className="h-4 w-4 text-slate-400" /> Job Title *</Label>
                    <Input name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Senior Marketing Manager" required data-testid="featured-title" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Building2 className="h-4 w-4 text-slate-400" /> Employer *</Label>
                    <Input name="employer" value={formData.employer} onChange={handleChange} placeholder="e.g. Renfrewshire Council" required data-testid="featured-employer" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><MapPin className="h-4 w-4 text-slate-400" /> Location *</Label>
                    <Input name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Paisley" required data-testid="featured-location" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><BadgePoundSterling className="h-4 w-4 text-slate-400" /> Salary</Label>
                    <Input name="salary" value={formData.salary} onChange={handleChange} placeholder="e.g. £40,000 - £45,000" data-testid="featured-salary" />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><LinkIcon className="h-4 w-4 text-slate-400" /> Apply Link (URL) *</Label>
                  <Input name="link" type="url" value={formData.link} onChange={handleChange} placeholder="https://..." required data-testid="featured-link" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><FileText className="h-4 w-4 text-slate-400" /> Description</Label>
                  <Textarea name="description" value={formData.description} onChange={handleChange} placeholder="Brief description of the role, requirements, perks..." rows={4} data-testid="featured-desc" />
                </div>

                {/* Image Upload */}
                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><ImageIcon className="h-4 w-4 text-slate-400" /> Company Logo / Image</Label>
                  <div className="flex items-center gap-4">
                    {imagePreview ? (
                      <div className="relative w-24 h-24 rounded-lg border overflow-hidden bg-white">
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center" data-testid="remove-image-btn">x</button>
                      </div>
                    ) : (
                      <label htmlFor="featured-image" className="flex flex-col items-center justify-center w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 cursor-pointer hover:border-amber-400 transition-colors bg-slate-50" data-testid="image-upload-area">
                        <Upload className="h-5 w-5 text-slate-400" />
                        <span className="text-xs text-slate-400 mt-1">Upload</span>
                      </label>
                    )}
                    <input id="featured-image" type="file" accept="image/*" onChange={handleImageChange} className="hidden" data-testid="featured-image-input" />
                    <p className="text-sm text-slate-500">Optional. JPEG, PNG, WebP. Max 5MB.</p>
                  </div>
                </div>

                <hr />

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><User className="h-4 w-4 text-slate-400" /> Your Name *</Label>
                    <Input name="contact_name" value={formData.contact_name} onChange={handleChange} required data-testid="featured-contact-name" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 mb-1.5"><Mail className="h-4 w-4 text-slate-400" /> Your Email *</Label>
                    <Input name="contact_email" type="email" value={formData.contact_email} onChange={handleChange} required data-testid="featured-contact-email" />
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-1.5"><Building2 className="h-4 w-4 text-slate-400" /> Business Name (if different from employer)</Label>
                  <Input name="business_name" value={formData.business_name} onChange={handleChange} data-testid="featured-business" />
                </div>

                <Button type="submit" disabled={submitting} className="w-full h-12 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white text-base shadow-lg" data-testid="featured-submit-btn">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</> : <>Pay £30 & Feature Job <Crown className="h-4 w-4 ml-2" /></>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturedJobPage;
